import Input from '@eduzz/houston-ui/Forms/Input';
import Select from '@eduzz/houston-ui/Forms/Select';
import { FormControlLabel, FormHelperText, Grid, InputLabel, Switch, Tooltip } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import CharacterCounter from 'components/CharacterCounter';
import CropUploader from 'components/CropUploader';
import CustomMaskedInput from 'components/MaskedInput';
import { constants } from 'helpers';
import { EPartnerCode } from 'interfaces/IProduct';
import React, { Component } from 'react';
import { Wysiwyg } from 'v2/components/Shared/Wysiwyg';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import { FormContent } from '../FormContent';
import styles from '../styles';
import BlockedFieldTooltipProps from './blockedTooltipProps';
import MarketplaceValidation from './marketplaceValidation';
import Validations from './validations';

import type IProduct from 'interfaces/IProduct';

interface IProps {
  loading: boolean;
  fade?: boolean;
  product?: IProduct;
  handleImageChange?: any;
  handleUploadEvents?: any;
  modifieds?: any[];
  editing?: any;
  handleChange?: any;
  categoryList?: any;
  onChangeRequiredFields?: any;
  classes?: any;
  variations?: IProduct[];
}
interface IStates {
  showErrors: boolean;
  validationErrors: any;
}

class General extends Component<IProps, IStates> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      showErrors: false,
      validationErrors: [],
    };
  }
  errorFor = (field: any) => {
    return this.state.validationErrors[field] || '';
  };

  isValid = (field: any) => {
    return this.state.validationErrors[field] ? true : false;
  };
  componentDidUpdate(prevProps: IProps) {
    if ((!this.props.loading && prevProps.loading) || this.props.product !== prevProps.product) {
      let { product, onChangeRequiredFields, variations } = this.props;
      let newState = { ...this.state };
      newState.validationErrors = run(product, Validations);

      if (variations && variations.some((variation) => variation.moderation_status === 'A') && !product.parent_id) {
        const marketplaceErrors = run(product, MarketplaceValidation);
        Object.assign(newState.validationErrors, marketplaceErrors);
      }
      this.setState(newState);
      if (onChangeRequiredFields) {
        onChangeRequiredFields('generalRequiredFields', newState.validationErrors ? Object.keys(newState.validationErrors).length : 0);
      }
    }
  }

  htmlDecode(input: any) {
    let doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent;
  }

  render() {
    const { loading, product, handleImageChange, handleChange, categoryList, variations } = this.props;
    const isVariation = product.isVariation();

    const isMarketplaceBeta = eduzzAccountsService.verifyUserHasBetaModule('marketplace_eduzz');
    const isAlumy = product.cnt_partner_cod === EPartnerCode.ALUMY;
    const isBlinket = product.cnt_partner_cod === EPartnerCode.BLINKET;
    const noEditMessage = 'Só é possível alterar o campo em configurações gerais';
    const categories = Array.isArray(categoryList) ? categoryList : [];
    const approvedVariations = variations?.filter((variation) => variation.moderation_status === 'A');
    const hasNutrorDelivery = product.getDeliveries().some((delivery) => delivery.method_name === 'Nutror');

    return (
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12}>
          <FormContent title='Dados básicos do produto' loading={loading}>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12}>
                <BlockedFieldTooltipProps product={product} enabled={isAlumy || isBlinket}>
                  <div>
                    <Input
                      helperText={'Título curto do que está sendo vendido: Especialista em Vendas 1.0'}
                      label='Título do produto'
                      id='title'
                      errorMessage={this.isValid('title') ? this.errorFor('title') : null}
                      fullWidth
                      value={product.title}
                      disabled={isAlumy || isBlinket}
                      onChange={(e: any) => handleChange('title', e)}
                    />
                  </div>
                </BlockedFieldTooltipProps>
              </Grid>
              <Grid item xs={12}>
                <Tooltip title='Tipo de produto não pode ser alterado'>
                  <Select
                    label='Tipo do produto'
                    helperText='Ex: tipo do produto vendido: infoproduto, físico, serviço, ticket...'
                    //id='type'
                    fullWidth
                    disabled
                    value={product.type}
                    onChange={(e: any) => handleChange('type', e)}
                  >
                    <Select.Option value={1} disabled>
                      Infoproduto
                    </Select.Option>
                    <Select.Option value={2} disabled>
                      Serviço
                    </Select.Option>
                    <Select.Option value={3} disabled>
                      Produto Físico
                    </Select.Option>
                    <Select.Option value={4} disabled>
                      Ticket
                    </Select.Option>
                  </Select>
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Select
                  multiple
                  errorMessage={this.isValid('categories') ? this.errorFor('categories') : null}
                  value={product.getIdCategories()}
                  onChange={(e: any) => handleChange('categories', e)}
                  label='Categorias'
                >
                  <Select.Option>Limpar</Select.Option>
                  {categories.map((item: any) => (
                    <Select.Option key={'catmn' + item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12}>
                <BlockedFieldTooltipProps product={product} enabled={isAlumy || isBlinket}>
                  <div>
                    <CharacterCounter
                      value={this.htmlDecode(product.description)}
                      limits={4000}
                      minimum={isBlinket ? 50 : 100}
                      helperText={
                        this.isValid('description')
                          ? this.errorFor('description')
                          : 'Pequena descrição sobre seu produto/serviço, seus benefícios, etc'
                      }
                      disabled={isAlumy || isBlinket}
                      label='Descrição do produto'
                      error={this.isValid('description')}
                      onChange={(e: any) => handleChange('description', e.target.value)}
                      singleSpace={true}
                    />
                  </div>
                </BlockedFieldTooltipProps>
              </Grid>
              <Grid item xs={12}>
                <InputLabel error={this.isValid('image_id')}>Imagem do produto</InputLabel>
                <CropUploader name='image' id='name' image={product.image} onChange={handleImageChange} fileName='image_id' uploadType='cnt_foto' />
                <FormHelperText style={{ color: '#dc3f53' }}>{this.isValid('image_id') ? this.errorFor('image_id') : null}</FormHelperText>
              </Grid>
            </Grid>
          </FormContent>
        </Grid>
        <Grid item xs={12}>
          <FormContent title='Detalhes do produto' loading={loading}>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12}>
                <BlockedFieldTooltipProps product={product} enabled={isBlinket}>
                  <FormControlLabel
                    label='Produto será lançamento'
                    disabled={isBlinket}
                    control={
                      <Switch color='default' checked={product.prelaunch} onChange={(e: any) => handleChange('prelaunch', e.target.checked)} />
                    }
                  />
                </BlockedFieldTooltipProps>
              </Grid>
              {product.prelaunch && (
                <>
                  <Grid item xs={12}>
                    <BlockedFieldTooltipProps product={product} enabled={isBlinket}>
                      <div>
                        <DatePicker
                          fullWidth
                          autoOk
                          disableToolbar
                          variant='inline'
                          format={isBlinket ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy'}
                          placeholder='DD/MM/YYYY'
                          value={product.launch_date}
                          disabled={isBlinket}
                          onChange={(date) => handleChange('launch_date', date)}
                          label='Data do lançamento'
                          helperText='Se o lançamento estiver ativo o checkout ficará indisponível até a data informada.'
                          id='launch_date'
                          InputProps={{
                            disableUnderline: true,
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          minDate={new Date()}
                        />
                      </div>
                    </BlockedFieldTooltipProps>
                  </Grid>
                  <Grid item xs={12}>
                    <BlockedFieldTooltipProps product={product} enabled={isBlinket || isAlumy}>
                      <div>
                        <CustomMaskedInput
                          fullWidth
                          disableUnderline
                          label='Número de vendas esperadas'
                          mask='number'
                          value={String(product.sales_expectation)}
                          disabled={isAlumy || isBlinket}
                          onChange={(value: any) => handleChange('sales_expectation', value)}
                          helperText='Ex: Quantas vendas estima fazer durante o lançamento?'
                        />
                      </div>
                    </BlockedFieldTooltipProps>
                  </Grid>
                </>
              )}
              {!product.isMainWithChildren() && (
                <>
                  <Grid item xs={12}>
                    <BlockedFieldTooltipProps product={product} enabled={isBlinket}>
                      <FormControlLabel
                        label='Validade do produto'
                        disabled={isBlinket}
                        control={
                          <Switch
                            color='default'
                            checked={product.has_expiration_date}
                            onChange={(e: any) => handleChange('has_expiration_date', e.target.checked)}
                          />
                        }
                      />
                    </BlockedFieldTooltipProps>
                  </Grid>
                  {product.has_expiration_date && (
                    <Grid item xs={12}>
                      <BlockedFieldTooltipProps product={product} enabled={isBlinket}>
                        <div>
                          <DatePicker
                            fullWidth
                            disableToolbar
                            variant='inline'
                            format={isBlinket ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy'}
                            autoOk
                            label='Data de validade'
                            helperText='Data final da comercialização do conteúdo'
                            id='expiration_date'
                            placeholder='DD/MM/YYYY'
                            value={product.expiration_date}
                            onChange={(date: any) => handleChange('expiration_date', date)}
                            disabled={!product.has_expiration_date || isBlinket}
                            InputProps={{
                              disableUnderline: true,
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            minDate={new Date()}
                          />
                        </div>
                      </BlockedFieldTooltipProps>
                    </Grid>
                  )}
                </>
              )}
              {isMarketplaceBeta && hasNutrorDelivery && (
                <>
                  {!isVariation && product.moderation_status === 'A' && (
                    <Grid item xs={12}>
                      <BlockedFieldTooltipProps product={product} enabled={isAlumy || isBlinket}>
                        <div>
                          <FormControlLabel
                            label='Apresentar no Marketplace do site da Eduzz'
                            id='marketplace'
                            disabled={isAlumy || isBlinket}
                            control={
                              <Switch
                                color='default'
                                checked={product.marketplace}
                                onChange={(e: any) => handleChange('marketplace', e.target.checked)}
                              />
                            }
                          />
                          <FormHelperText>Ao ativar esta opção o seu produto será apresentado no site da Eduzz na opção Marketplace</FormHelperText>
                        </div>
                      </BlockedFieldTooltipProps>
                    </Grid>
                  )}
                  {product.marketplace && !isVariation && approvedVariations?.length >= 1 && (
                    <Grid item xs={12}>
                      <div>
                        <Select
                          label='Variação do produto'
                          id='marketplace_variation'
                          helperText='Selecione qual variação do produto será apresentada no Marketplace'
                          errorMessage={this.isValid('marketplace_variation') ? this.errorFor('marketplace_variation') : null}
                          fullWidth
                          value={product.marketplace_variation}
                          onChange={(e: any) => handleChange('marketplace_variation', e)}
                        >
                          {approvedVariations?.map((variation) => (
                            <Select.Option key={variation.id} value={variation.id}>
                              {variation.title}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </Grid>
                  )}
                </>
              )}
              {[constants.INFO, constants.TICKET].includes(product.type) && (
                <Grid item xs={12}>
                  <Tooltip
                    disableFocusListener={!isVariation}
                    disableHoverListener={!isVariation}
                    disableTouchListener={!isVariation}
                    title={noEditMessage}
                  >
                    <Input
                      label='Nome do autor'
                      helperText='Ex: José Pereira'
                      id='author_name'
                      fullWidth
                      disabled={isVariation}
                      value={product.author_name}
                      onChange={(e) => handleChange('author_name', e)}
                    />
                  </Tooltip>
                </Grid>
              )}
              {product.type === constants.INFO && !product.isMainWithChildren() && (
                <Grid item xs={12}>
                  {this.isValid('program_content') ? (
                    <InputLabel style={{ paddingBottom: '0.5rem', color: '#dc3f53' }}>O que contém o seu produto/serviço</InputLabel>
                  ) : (
                    <InputLabel style={{ paddingBottom: '0.5rem' }}>O que contém o seu produto/serviço</InputLabel>
                  )}
                  <Wysiwyg value={product.program_content} onChange={(v: string) => handleChange('program_content', v)} />
                  {this.isValid('program_content') ? (
                    <FormHelperText style={{ color: '#dc3f53' }}>{this.errorFor('program_content')}</FormHelperText>
                  ) : (
                    <FormHelperText>Ex: descrição sobre os componentes do produto. (Máximo de 4000 caracteres)</FormHelperText>
                  )}
                </Grid>
              )}
            </Grid>
          </FormContent>
        </Grid>

        {!product.isMainWithChildren() && (
          <Grid item xs={12}>
            <FormContent title='Suporte e Reembolso' loading={loading}>
              <Grid container spacing={2} alignItems='center'>
                {product.type === constants.INFO && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      label='Aprovação automática do reembolso'
                      control={
                        <Switch color='default' checked={product.auto_refund} onChange={(e: any) => handleChange('auto_refund', e.target.checked)} />
                      }
                    />
                    <FormHelperText>
                      Essa opção ativada autoriza que os reembolsos solicitados pelo comprador até o prazo configurado sejam realizados
                      automaticamente
                    </FormHelperText>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <BlockedFieldTooltipProps product={product} enabled={isBlinket}>
                    <Input
                      label='E-mail de suporte'
                      helperText={'Email que receberá os e-mails de contato'}
                      id='support_email'
                      fullWidth
                      errorMessage={this.isValid('support_email') ? this.errorFor('support_email') : null}
                      value={product.support_email}
                      disabled={isBlinket}
                      onChange={(e) => handleChange('support_email', e)}
                    />
                  </BlockedFieldTooltipProps>
                </Grid>
                <Grid item xs={12}>
                  <Input
                    label='Prazo para reembolso'
                    fullWidth
                    errorMessage={this.isValid('refund_days') ? this.errorFor('refund_days') : null}
                    value={product.refund_days}
                    onChange={(e) => handleChange('refund_days', e)}
                    helperText={'Prazo que o cliente poderá pedir reembolso. Mínimo de 7 dias'}
                  />
                </Grid>
              </Grid>
            </FormContent>
          </Grid>
        )}
        {product.type === constants.PHYSICAL && !product.isMainWithChildren() && (
          <Grid item xs={12}>
            <FormContent title='Estoque' loading={loading}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12}>
                  <Input
                    label='Quantidade de estoque'
                    fullWidth
                    errorMessage={this.isValid('stock') ? this.errorFor('stock') : null}
                    value={String(product.stock)}
                    onChange={(e) => handleChange('stock', e)}
                  />
                </Grid>

                {product.stock > 0 && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      label='Notificar quando estoque estiver acabando'
                      control={
                        <Switch
                          color='default'
                          checked={product.notify_stock > 0}
                          onChange={(e: any) =>
                            handleChange('notify_stock', e.target.checked ? (product.notify_stock > 0 ? product.notify_stock : 1) : 0)
                          }
                        />
                      }
                    />
                  </Grid>
                )}
                {product.stock > 0 && product.notify_stock > 0 && (
                  <Grid item xs={12}>
                    <CustomMaskedInput
                      fullWidth
                      disableUnderline
                      label='Notificar ao chegar em'
                      helperText={this.isValid('notify_stock') ? this.errorFor('notify_stock') : ''}
                      mask='number'
                      error={this.isValid('notify_stock')}
                      value={String(product.notify_stock)}
                      onChange={(value: any) => handleChange('notify_stock', value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </FormContent>
          </Grid>
        )}
      </Grid>
    );
  }
}

export default styles(General);
